import React from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Users } from "lucide-react";
import AuthImage from "@/components/AuthImage";

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Editorial event card. Tappable surface routes to /events/:id.
 * Compact (no nested controls) so it's safe to drop into any list.
 */
export default function EventCard({ event }) {
  const full = event.attendee_count >= event.capacity;
  return (
    <Link
      to={`/events/${event.id}`}
      className="block border border-white/10 hover:border-[#d4af37]/40 transition rounded-md overflow-hidden bg-[#141414]"
      data-testid={`event-card-${event.id}`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr]">
        <div className="bg-black h-40 sm:h-full">
          {event.image_path ? (
            <AuthImage path={event.image_path} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a]">
              <Calendar size={28} strokeWidth={1} className="text-[#d4af37]/40" />
            </div>
          )}
        </div>
        <div className="p-4 space-y-2 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#d4af37]">
            {event.creator?.role === "admin" ? "Concierge event" : "Member event"} · by {event.creator?.name || "—"}
          </p>
          <h3 className="font-serif text-xl leading-tight truncate">{event.title}</h3>
          <div className="flex items-center gap-2 text-xs text-white/60">
            <Calendar size={12} strokeWidth={1.5} className="text-white/40 shrink-0" />
            <span className="truncate">{formatWhen(event.starts_at)}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-white/60">
            <MapPin size={12} strokeWidth={1.5} className="text-white/40 shrink-0" />
            <span className="truncate">{event.location}</span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <div className="flex items-center gap-2 text-xs text-white/40">
              <Users size={12} strokeWidth={1.5} />
              {event.attendee_count} / {event.capacity}
            </div>
            <span
              className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${
                event.going
                  ? "border-[#d4af37]/60 text-[#d4af37]"
                  : full
                  ? "border-red-500/40 text-red-300"
                  : "border-white/15 text-white/60"
              }`}
              data-testid={`event-card-status-${event.id}`}
            >
              {event.going ? "Going" : full ? "Full" : "RSVP"}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
